import { gql } from '@apollo/client';

/**
 * Query to get total screens count for the current account/team.
 * Used for pre-checking screen limits before pairing a new device.
 */
export const GET_TOTAL_SCREENS_COUNT = gql`
  query GetTotalScreensCount($teamId: String, $subAccountId: String) {
    getTotalScreensCount(teamId: $teamId, subAccountId: $subAccountId)
  }
`;

/**
 * Response type for getTotalScreensCount query.
 * Backend returns JSONObject scalar — Apollo delivers it as a plain JS object.
 */
export interface TotalScreensCountResponse {
  getTotalScreensCount: {
    totalReserved: number;
    totalUsed: number;
    totalReservedNormal: number;
    minReserved: number;
    maxTeamQuantity: number;
    totalTeamUsed: number;
  };
}

// --- Room Signage license allocation (OptiEdge MDM) ---
// Reserves a chunk of the customer's screen-license pool for room
// integrations (ANY vendor — Zoom / Teams / Webex / Meet share this cap).
// Atomic on the backend: Mongo update + Redis write-through to
// optisigns:room-cap:{accountId} so rest-api claim sees the new cap on the
// next session start.
//
// Rooms-quota simplification (2026-05-27): the prior `vendor` arg was
// removed and per-vendor `*MaxQuantity` fields are no longer projected.
// The per-vendor schema fields linger on `Company.limitation` for back-
// compat with older clients but our queries don't fetch them. Per-vendor
// USAGE is still queryable via `device.feature.is{Vendor}Room`.

export const SET_ROOMS_LICENSE_ALLOCATION = gql`
  mutation SetRoomsLicenseAllocation($count: Int!) {
    setRoomsLicenseAllocation(count: $count) {
      _id
      limitation {
        maxQuantity
        quantity
        roomsQuantity
        roomsGraceLicense
      }
    }
  }
`;

export const GET_ROOMS_LICENSE_ALLOCATION = gql`
  query GetRoomsLicenseAllocation {
    getCompanyInfo {
      _id
      limitation {
        maxQuantity
        quantity
        roomsQuantity
        roomsGraceLicense
      }
    }
  }
`;

/**
 * Company profile fields fragment — shared between query and mutation.
 */
const COMPANY_PROFILE_FIELDS = gql`
  fragment CompanyProfileFields on Company {
    _id
    name
    industry
    phoneNumber
    contact {
      name
      phoneNumber
      email
    }
    location {
      address1
      address2
      city
      zip
      state
      country
    }
    partner {
      name
      locked
    }
    featureData {
      dateFormat
      timeFormat
      numberFormat
      scaleTemperature
      windSpeedUnit
      defaultValueType
    }
  }
`;

/**
 * Dedicated query for the Company Profile page.
 * Separate from GET_COMPANY_INFO (auth-time) to avoid bloating the auth payload
 * with contact/location/partner fields that only this page needs.
 */
export const GET_COMPANY_PROFILE = gql`
  ${COMPANY_PROFILE_FIELDS}
  query GetCompanyProfile {
    getCompanyInfo {
      ...CompanyProfileFields
    }
  }
`;

/**
 * Single mutation for both profile saves and display format saves.
 * - Profile save: sends name, industry, phoneNumber, contact, location, partner
 * - Format save: sends featureData only
 */
export const UPDATE_COMPANY_INFO = gql`
  ${COMPANY_PROFILE_FIELDS}
  mutation UpdateCompanyInfo($updateData: UpdateCompanySettingInput!) {
    updateCompanyInfo(updateData: $updateData) {
      ...CompanyProfileFields
    }
  }
`;

/**
 * Preferences page fields fragment — feature flags, featureData prefs, limitation.
 * Separate from profile fields to avoid bloating auth or profile queries.
 */
const COMPANY_PREFERENCES_FIELDS = gql`
  fragment CompanyPreferencesFields on Company {
    _id
    feature {
      branding
      remoteCommand
      secureLink
      securityAuditLogging
      wifiFallback
      enforceSSO
      disableVideoConversion
      enforcePasswordComplexity
      folderLevelSecurity
      feedsCreate
      feedsManage
      feedsCategories
    }
    featureData {
      language
      defaultPLDuration
      defaultPLScaleImage
      defaultPLScaleVideo
      defaultPLScaleDocument
      defaultPLTransition
      targetTagsDefault
      isSecurityAuditLogging
      auditLogsAllowIp
      isRemoteCommand
      secureLink
      enableWifiFallback
      wifiFallbackRetryInterval
      wifiFallbackMaxRetryDay
      defaultNavIconPosition
      defaultNavIconStyle
      defaultNavIconColorType
      defaultNavIconColorCode
      defaultNavIconSizeType
      defaultNavIconSize
      defaultNavBackgroundColor
      defaultNavOpacity
    }
    limitation {
      maxQuantity
      disableDownload
    }
  }
`;

/**
 * Dedicated query for the Preferences page.
 * Returns feature flags, featureData prefs, and limitation fields.
 */
export const GET_COMPANY_PREFERENCES = gql`
  ${COMPANY_PREFERENCES_FIELDS}
  query GetCompanyPreferences {
    getCompanyInfo {
      ...CompanyPreferencesFields
    }
  }
`;

/**
 * Mutation for saving preferences. Returns the same fields to update Apollo cache.
 */
export const UPDATE_COMPANY_PREFERENCES = gql`
  ${COMPANY_PREFERENCES_FIELDS}
  mutation UpdateCompanyPreferences($updateData: UpdateCompanySettingInput!) {
    updateCompanyInfo(updateData: $updateData) {
      ...CompanyPreferencesFields
    }
  }
`;

/**
 * Delete the current user's account. Backend handles Stripe cancellation,
 * marks company for deletion, and schedules data cleanup.
 * Only OWNER role can execute (enforced by backend service).
 */
export const DELETE_COMPANY = gql`
  mutation DeleteCompany {
    deleteCompany
  }
`;

// ---------------------------------------------------------------------------
// Response types
// ---------------------------------------------------------------------------

export interface CompanyProfileData {
  _id: string;
  name: string;
  industry: string;
  phoneNumber: string;
  featureData: {
    defaultValueType: string;
    scaleTemperature: string;
    dateFormat: string;
    timeFormat: string;
    numberFormat: string;
    windSpeedUnit: string;
  };
  contact: { name: string; phoneNumber: string; email: string } | null;
  location: {
    address1: string;
    address2: string;
    city: string;
    state: string;
    country: string;
    zip: string;
  } | null;
  partner: { name: string; locked: boolean } | null;
}

export interface CompanyProfileResponse {
  getCompanyInfo: CompanyProfileData;
}

export interface UpdateCompanyInfoResponse {
  updateCompanyInfo: CompanyProfileData;
}

export interface DeleteCompanyResponse {
  deleteCompany: boolean;
}

/**
 * Link an OptiDev workspace to this account.
 * Called after OptiPrime provision returns a workspace_id.
 * Admin/Owner only (enforced by backend).
 */
export const SET_OPTIDEV_WORKSPACE = gql`
  mutation SetOptiDevWorkspace($workspaceId: String!) {
    setOptiDevWorkspace(workspaceId: $workspaceId)
  }
`;

export interface SetOptiDevWorkspaceResponse {
  setOptiDevWorkspace: boolean;
}

/**
 * Write-once store of an A/B experiment variant on the company, keyed by
 * experiment flag (e.g. key='onboarding-text', variant='b'). First-writer-wins
 * on the backend, so a company's variant is sticky forever. Returns the
 * company's full experiments map. Generic, reusable by any experiment.
 */
export const SET_EXPERIMENT_VARIANT = gql`
  mutation SetExperimentVariant($key: String!, $variant: String!) {
    setExperimentVariant(key: $key, variant: $variant)
  }
`;

export interface SetExperimentVariantResponse {
  /** The company's full experiments map, keyed by experiment flag. */
  setExperimentVariant: Record<string, string>;
}

/**
 * OptiGrowth self-owned decider — resolve THIS company's arm for an experiment,
 * deciding it server-side on companyId and persisting it write-once. Returns the
 * variant string, or `null` when the company is not enrolled (unknown key /
 * ineligible / held out). `null` is NOT control — it means "no arm". Sticky once
 * assigned. The general consume seam (`useExperimentVariant`) calls this.
 */
export const RESOLVE_EXPERIMENT_VARIANT = gql`
  mutation ResolveExperimentVariant($key: String!) {
    resolveExperimentVariant(key: $key)
  }
`;

export interface ResolveExperimentVariantResponse {
  /** The assigned arm, or null when not enrolled. */
  resolveExperimentVariant: string | null;
}

// --- Company branding for custom email sender ---
// company.branding (MongoDB) ≠ brandingData (from domain collection).
// Shared between External Coms and Proof of Play.

export const COMPANY_BRANDING_QUERY = gql`
  query CompanyBranding {
    companyBranding {
      email
      emailFullname
      isVerifiedEmail
    }
  }
`;

export const UPDATE_BRANDING_EMAIL = gql`
  mutation UpdateBrandingEmail($payload: UpdateBrandingEmailInput!) {
    updateBrandingEmail(payload: $payload) {
      isVerifiedEmail
    }
  }
`;

export const SEND_BRANDING_TEST_EMAIL = gql`
  mutation SendBrandingTestEmail($payload: SendBrandingTestEmailInput!) {
    sendBrandingTestEmail(payload: $payload)
  }
`;

// Check SES verification status ("click here to refresh")
export const CHECK_BRANDING_VERIFICATION = gql`
  mutation CheckBrandingVerification {
    checkBrandingVerification {
      email
      emailFullname
      isVerifiedEmail
    }
  }
`;
